import type { ComponentPropsWithRef, JSX, ForwardedRef, ReactNode, MouseEvent } from 'react';
import { forwardRef } from 'react';
import { styled } from 'styled-components';
import { isNotNil, isNotUndefined } from '@wistia/type-guards';
import type { ButtonVariants } from './buttonStyles';
import type { ColorSchemeTypes } from '../ColorSchemeWrapper';
import type { LinkProps, LinkTypes } from '../Link/Link';
import type { ResponsiveObject } from '../../private/hooks/useResponsiveProp/useResponsiveProp';
import { buttonResetCss } from '../../css/buttonResetCss';
import { buttonBaseStyles, buttonSizeStyles, buttonVariantsStyles } from './buttonStyles';
import { getColorScheme } from '../../private/helpers/getColorScheme';
import { Icon } from '../Icon';
import { Link } from '../Link';
import { useResponsiveProp } from '../../private/hooks/useResponsiveProp';

export type ButtonSizes = 'lg' | 'md' | 'sm' | 'xl';

// children and onClick types get mangled in docs so setting @type manually fixes that
export type BaseButtonProps = {
  /**
   * Text that appears inside of the button
   *
   * @type ReactNode
   */
  children: ReactNode;
  /**
   * Sets the [color scheme](../?path=/docs/docs-color-schemes--docs)
   */
  colorScheme?: ColorSchemeTypes;
  /**
   * Disables the button
   */
  disabled?: boolean;
  /**
   * Show a spinner when the button is in a loading state
   */
  isLoading?: boolean;
  /**
   * Icon to display on the left side of the button. Must be an instance of [Icon](?path=/docs/components-icon--docs)
   */
  leftIcon?: ReactNode | undefined;
  /**
   * Callback function invoked when the button is clicked
   *
   * @type (event: MouseEvent) => void
   */
  onClick?: ((event: MouseEvent) => void) | undefined;
  /**
   * @ignore
   * Force a button into a particular css state (for debugging)
   */
  forceState?: 'active' | 'focus' | 'hover' | undefined;
  /**
   * If true, the button will fill its container
   *
   * @type boolean | ResponsiveObject<boolean>
   */
  fullWidth?: ResponsiveObject<boolean> | boolean;
  /**
   * Provides a button without any styling (useful for wrapping another element)
   */
  unstyled?: boolean | undefined;
  /**
   * Icon to display on the right side of the button. Must be an instance of [Icon](?path=/docs/components-icon--docs)
   */
  rightIcon?: ReactNode | undefined;
  /**
   * The size of button to display. Can be used as a [responsive prop](/docs/ui-docs-responsive-props--docs)
   *
   * @type ResponsiveObject<'lg' | 'md' | 'sm' | 'xl'>
   */
  size?: ButtonSizes | ResponsiveObject<ButtonSizes>;
  /**
   * A visual style for the button
   */
  variant?: ButtonVariants;
};

export type ButtonAsLinkProps = BaseButtonProps &
  ComponentPropsWithRef<'a'> &
  LinkProps & {
    href: string | undefined;
    children: ReactNode;
    /**
     * This just allows pathrough to beforeAction for link
     * @ignore
     */
    beforeAction?: (() => Promise<void>) | (() => void);
    /**
     * This just allows pathrough to beforeAction for link
     * @ignore
     */
    type?: LinkTypes;
  };

export type ButtonAsButtonProps = BaseButtonProps &
  ComponentPropsWithRef<'button'> & {
    /**
     * If supplied, the button will render as a link
     */
    href?: never;
    type?: 'button' | 'reset' | 'submit';
    children: ReactNode;
  };

export const isLink = (props: Omit<ButtonProps, 'children'>): props is ButtonAsLinkProps =>
  isNotUndefined(props.href);

type StyledButtonProps = {
  $colorScheme: ColorSchemeTypes;
  $size: ButtonSizes;
  $variant: ButtonVariants;
  $unstyled: boolean;
  $fullWidth: boolean;
};

const StyledButton = styled.button<StyledButtonProps>`
  ${buttonResetCss}
  ${({ $colorScheme }) => getColorScheme($colorScheme)}
  ${({ $size }) => buttonSizeStyles[$size]}
  ${({ $variant }) => buttonVariantsStyles[$variant]}
  ${({ $unstyled }) => !$unstyled && buttonBaseStyles}
  ${({ $fullWidth }) => $fullWidth && 'width: 100%;'}
  text-align: center;
`;

type StyledButtonContentProps = {
  $isLoading: boolean;
  $hasLeftIcon: boolean;
  $hasRightIcon: boolean;
};

const StyledButtonContent = styled.div<StyledButtonContentProps>`
  align-items: center;
  display: inline-flex;
  flex-grow: 1;
  justify-content: center;
  opacity: ${({ $isLoading }) => ($isLoading ? 0 : 1)};
  padding: 0 var(--button-label-padding-horizontal);
  gap: var(--button-label-gap);

  ${({ $hasLeftIcon }) => $hasLeftIcon && 'padding-left: 0;'}
  ${({ $hasRightIcon }) => $hasRightIcon && 'padding-right: 0;'}
`;

const StyledButtonContentLabel = styled.span<{ $fullWidth: boolean }>`
  flex: ${({ $fullWidth }) => ($fullWidth ? 'initial' : '1')};
`;

const StyledButtonLoading = styled.div`
  position: absolute;
  display: flex;
`;

export type ButtonProps = ButtonAsButtonProps | ButtonAsLinkProps;

const ButtonContent = ({
  isLoading = false,
  leftIcon,
  rightIcon,
  children,
  fullWidth = false,
}: Pick<ButtonProps, 'children' | 'isLoading' | 'leftIcon' | 'rightIcon'> & {
  fullWidth: boolean;
}) => {
  return (
    <>
      {isLoading ? (
        <StyledButtonLoading>
          {/* <LiveMessage
              aria-live="assertive"
              message="Button has been disabled while action is in progress"
            /> */}
          <Icon type="spinner" />
        </StyledButtonLoading>
      ) : null}
      <StyledButtonContent
        $hasLeftIcon={isNotNil(leftIcon)}
        $hasRightIcon={isNotNil(rightIcon)}
        $isLoading={isLoading}
      >
        {leftIcon ?? null}
        <StyledButtonContentLabel $fullWidth={fullWidth}>{children}</StyledButtonContentLabel>
        {rightIcon ?? null}
      </StyledButtonContent>
    </>
  );
};

/**
 * Button component is used to trigger an action or event,
 * such as submitting a form, opening a Dialog, canceling an
 * action, or performing a delete operation. It replaces the HTML `<button>` element,
 * unless an `href` attribute is passed, in which it will render an `<a>` element.
 */
export const Button = forwardRef<HTMLAnchorElement | HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      forceState,
      className,
      leftIcon,
      rightIcon,
      colorScheme = 'inherit',
      disabled = false,
      isLoading = false,
      unstyled = false,
      onClick,
      fullWidth = false,
      size = 'md',
      variant = 'solid',
      ...props
    }: ButtonProps,
    ref,
  ): JSX.Element => {
    const responsiveFullWidth = useResponsiveProp(fullWidth);
    const responsiveSize = useResponsiveProp(size);

    const isAriaDisabled = () => isLoading || disabled;

    const commonProps = {
      'aria-disabled': isAriaDisabled() || undefined,
      $colorScheme: colorScheme,
      $fullWidth: responsiveFullWidth,
      $size: responsiveSize,
      $unstyled: unstyled,
      $variant: variant,
      className,
    };

    if (forceState) {
      commonProps.className = `${className ?? ''} force-state-${forceState}`;
    }

    if (isLink(props)) {
      return (
        <StyledButton
          ref={ref as ForwardedRef<HTMLAnchorElement>}
          as={Link}
          disabled={isAriaDisabled()}
          {...props}
          {...commonProps}
        >
          {unstyled ? (
            children
          ) : (
            <ButtonContent
              fullWidth={responsiveFullWidth}
              isLoading={isLoading}
              leftIcon={leftIcon}
              rightIcon={rightIcon}
            >
              {children}
            </ButtonContent>
          )}
        </StyledButton>
      );
    }

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
      if (isAriaDisabled()) {
        event.preventDefault();
        return;
      }

      if (isNotNil(onClick)) {
        onClick(event);
      }
    };

    return (
      <StyledButton
        ref={ref as ForwardedRef<HTMLButtonElement>}
        onClick={handleClick}
        type="button"
        {...(props as ButtonAsButtonProps)}
        {...commonProps}
        data-wui-button={true}
      >
        {unstyled ? (
          children
        ) : (
          <ButtonContent
            fullWidth={responsiveFullWidth}
            isLoading={isLoading}
            leftIcon={leftIcon}
            rightIcon={rightIcon}
          >
            {children}
          </ButtonContent>
        )}
      </StyledButton>
    );
  },
);

Button.displayName = 'Button';
